import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid2 as Grid,
  Button,
  DialogActions,
  Tabs,
  Tab,
  Typography,
} from "@mui/material"

import { Formik } from "formik"
import { transformErrorForForm } from "../utils/utils"
import * as yup from "yup"
import { useTranslation } from "react-i18next"
import { useSequenzeImport, SequenzeImportPayload, SequenzeImportResponse } from "../hooks/sequenze"
import { MuiFileInput } from "mui-file-input"
import { useState, useCallback } from "react"
import { useSnackbarMessage } from "../hooks/snackbar"

type SupportiImportProps = {
  open: boolean
  onClose: () => void
}

const schema = yup.object().shape({
  file: yup.mixed().required("File is required"),
})

function hasErrors(response: SequenzeImportResponse): boolean {
  return response.errors && Object.keys(response.errors).length > 0
}

function ErrorItem({ row, errors }: { row: string; errors: Record<string, string> }) {
  const errorsAsList = Object.entries(errors).map(([key, error]) => `${key}: ${error}`)
  const { t } = useTranslation()

  const errorsAsString = errorsAsList.join(", ")

  return (
    <div key={row}>
      {t("general.riga")} {row}: {errorsAsString}
    </div>
  )
}

export default function SequenzeImport({ open, onClose }: SupportiImportProps) {
  const { t } = useTranslation()
  const { mutateAsync } = useSequenzeImport()
  const initialValues = {
    file: "",
    commit: false,
  } as unknown as SequenzeImportPayload

  const [currentTab, setCurrentTab] = useState<"file" | "commit">("file")
  const [currentResponse, setCurrentResponse] = useState<any>(null)
  const [currentErrors, setCurrentErrors] = useState<any>(null)
  const [currentFile, setCurrentFile] = useState<File | null>(null)

  const handleClose = useCallback(
    (reset: () => void) => {
      setCurrentTab("file")
      setCurrentResponse(null)
      setCurrentErrors(null)
      setCurrentFile(null)
      onClose()
      reset && reset()
    },
    [onClose]
  )

  const showMessage = useSnackbarMessage()

  return (
    <Formik
      validationSchema={schema}
      enableReinitialize
      initialValues={initialValues}
      onSubmit={async (values, formikBag) => {
        const submitValues = {
          file: values.file,
          commit: currentTab === "commit",
        } as SequenzeImportPayload
        console.log("Submitting import with values:", submitValues)
        try {
          const response = await mutateAsync(submitValues)

          setCurrentResponse(response)
          setCurrentFile(values.file)
          
          if (hasErrors(response)) {
            formikBag.setFieldValue("file", null)
            setCurrentErrors(response.errors)
          } else {
            setCurrentErrors(null)
            if (currentTab === "file") {
              setCurrentTab("commit")
            } else {
              handleClose(formikBag.resetForm)
              setCurrentFile(null)
              showMessage("Sequenze importate correttamente", "success")
            }
          }
        } catch (e) {
          console.error("Error in import submission", e)
          const localError = transformErrorForForm(e)
          
          if (currentTab === "file") {
            if('error' in localError) {
              console.log("Local error 'error' field:", localError, localError['error'])
              const errorMsg = localError['error'] as string
              showMessage("Errore durante l'importazione delle sequenze: " + errorMsg, "error")
            } else {
              formikBag.setErrors(localError)
            }
            
          } else {
            showMessage("Errore durante l'importazione delle sequenze", "error")
          }
        }
      }}
    >
      {({ handleSubmit, setFieldValue, values, resetForm }) => {
        return (
          <Dialog
            open={open}
            onClose={() => {
              handleClose(resetForm)
            }}
            fullWidth
            maxWidth="md"
          >
            <form onSubmit={handleSubmit} noValidate>
              <DialogTitle>Import Sequenze</DialogTitle>
              <DialogContent>
                <Tabs value={currentTab}>
                  <Tab value="file" label="Seleziona file" />
                  <Tab value="commit" label="Importa" />
                </Tabs>

                {currentTab === "file" && (
                  <Grid container spacing={1} sx={{ py: 2 }}>
                    <Grid size={12}>
                      <MuiFileInput
                        name="file"
                        label={t("ente.nome")}
                        required
                        margin="dense"
                        fullWidth
                        variant="outlined"
                        value={values.file}
                        onChange={(file) => {
                          if (file) {
                            setFieldValue("file", file)
                          }
                        }}
                      />
                    </Grid>
                    {currentFile && (
                      <Grid size={12}>
                        <Typography variant="body2" color="textSecondary">
                          {t("supporto.file_selezionato")}: {currentFile.name}
                        </Typography>
                      </Grid>
                    )}
                    {currentErrors && (
                      <>
                        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                          {t("supporto.errori_importazione")}
                        </Typography>
                        <Grid size={12} sx={{ maxHeight: 200, overflow: "auto", border: "1px solid #ccc", padding: 1 }}>
                          {Object.entries(currentErrors).map(([key, error]) => (
                            <ErrorItem key={key} row={key} errors={error as Record<string, string>} />
                          ))}
                        </Grid>
                      </>
                    )}
                  </Grid>
                )}

                {currentTab === "commit" && (
                  <Grid container spacing={1} sx={{ py: 2 }}>
                    {currentResponse.sequenze_update && currentResponse.sequenze_update.length > 0 && (
                      <Grid size={12}>
                        <div>
                          <b>{t("sequenza.sequenze_aggiornate")}</b>
                        </div>
                        {currentResponse.sequenze_update.map((sequenza: any, index: number) => (
                          <span key={index}>{sequenza}&nbsp;</span>
                        ))}
                      </Grid>
                    )}
                    {currentResponse.sequenze_new && currentResponse.sequenze_new.length > 0 && (
                      <Grid size={12}>
                        <div>
                          <b>{t("sequenza.sequenze_inserite")}</b>
                        </div>
                        {currentResponse.sequenze_new.map((sequenza: any, index: number) => (
                          <span key={index}>{sequenza}&nbsp;</span>
                        ))}
                      </Grid>
                    )}
                  </Grid>
                )}
              </DialogContent>

              <Button type="submit" color="primary" sx={{ display: "none" }}></Button>
              <DialogActions sx={{ justifyContent: "space-between" }}>
                <Button onClick={() => handleClose(resetForm)} color="primary" variant="text">
                  {t("general.annulla")}
                </Button>
                <Button type="submit" color="primary" variant="contained">
                  {t("general.importa")}
                </Button>
              </DialogActions>
            </form>
          </Dialog>
        )
      }}
    </Formik>
  )
}
